import { supabase } from "@/platform/lib/supabase";
import { fetchQuestionPageImages } from "@/tenants/usp/lib/page-image";
import { scheduleUspReviews } from "@/tenants/usp/features/review/api";

/**
 * Diagnóstico USP — busca 10 questões balanceadas do corpus usp_questions.
 * Funciona como demo público (sem login) E como diagnóstico real persistido
 * (com login): cria usp_sessions(kind='diagnosis') + usp_attempts + agenda
 * revisões SM-2 das erradas, alimentando o plano adaptativo.
 */

/** Cria sessão de diagnóstico persistida (apenas autenticada). */
export async function startDiagnosisSession(
  userId: string,
  questionIds: string[],
): Promise<string | null> {
  const { data, error } = await supabase
    .from("usp_sessions")
    .insert({
      user_id: userId,
      kind: "diagnosis",
      started_at: new Date().toISOString(),
      total_questions: questionIds.length,
      metadata: { question_ids: questionIds },
    })
    .select("id")
    .single();
  if (error || !data) {
    console.error("[usp-diag] create session", error);
    return null;
  }
  return data.id as string;
}

/** Registra resposta do diagnóstico (idempotência simples por tentativa). */
export async function recordDiagnosisAttempt(params: {
  userId: string;
  sessionId: string;
  questionId: string;
  selected: string;
  correctAnswer: string | null;
}): Promise<void> {
  const isCorrect = params.correctAnswer ? params.selected === params.correctAnswer : null;
  await supabase.from("usp_attempts").insert({
    user_id: params.userId,
    session_id: params.sessionId,
    question_id: params.questionId,
    answer: params.selected,
    is_correct: isCorrect,
    score: isCorrect ? 1 : 0,
  });
}

/** Finaliza diagnóstico: fecha sessão + agenda revisão das erradas. */
export async function finishDiagnosisSession(
  sessionId: string,
  userId: string,
  byDiscipline: Record<string, { total: number; correct: number }>,
  correct: number,
  wrongQuestionIds: string[],
): Promise<void> {
  await supabase
    .from("usp_sessions")
    .update({
      finished_at: new Date().toISOString(),
      correct_count: correct,
      metadata: { by_discipline: byDiscipline },
    })
    .eq("id", sessionId);
  if (wrongQuestionIds.length > 0) {
    await scheduleUspReviews({ userId, questionIds: wrongQuestionIds });
  }
}

export interface DemoQuestion {
  id: string;
  year: number;
  number: number;
  discipline: string;
  topic: string | null;
  cognitive_skill: string | null;
  question_type: string | null;
  difficulty: string | null;
  stem: string;
  has_base_text: boolean;
  base_text: string | null;
  has_image: boolean;
  has_graph: boolean;
  has_table: boolean;
  exam_paper_id: string | null;
  page_number: number | null;
  alternatives: { A: string; B: string; C: string; D: string; E: string };
  correct_answer: string | null;
  llm_confidence: number | null;
  pageImageUrl?: string;
}

const DEMO_SIZE = 10;

export async function fetchDemoQuestions(): Promise<DemoQuestion[]> {
  const { data, error } = await supabase
    .from("usp_questions")
    .select(
      "id, year, number, discipline, topic, cognitive_skill, question_type, difficulty, stem, has_base_text, base_text, has_image, has_graph, has_table, exam_paper_id, page_number, alternatives, correct_answer, llm_confidence",
    )
    .eq("kind", "objective")
    .not("correct_answer", "is", null)
    .not("llm_classified_at", "is", null)
    .limit(120);
  if (error) throw error;

  // Balanced sampling: 1 por disciplina (preferindo disciplinas com peso maior)
  const byDisc = new Map<string, DemoQuestion[]>();
  for (const q of data as DemoQuestion[]) {
    const arr = byDisc.get(q.discipline) ?? [];
    arr.push(q);
    byDisc.set(q.discipline, arr);
  }

  // shuffle each bucket
  for (const arr of byDisc.values()) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    }
  }

  // round-robin pick
  const disciplines = Array.from(byDisc.keys());
  const picked: DemoQuestion[] = [];
  let round = 0;
  while (picked.length < DEMO_SIZE && disciplines.length > 0) {
    let progress = false;
    for (const d of disciplines) {
      const bucket = byDisc.get(d);
      if (bucket && bucket[round]) {
        picked.push(bucket[round]!);
        progress = true;
        if (picked.length >= DEMO_SIZE) break;
      }
    }
    if (!progress) break;
    round++;
  }

  // Anexa URL da página renderizada para questões com visual.
  const images = await fetchQuestionPageImages(picked);
  for (const q of picked) {
    if (q.pageImageUrl === undefined && images[q.id]) q.pageImageUrl = images[q.id];
  }
  return picked;
}
